import React, { useMemo } from 'react';
import '../styles/LeadMapBubble.css';
import { colour, formatterCompact } from '../styles/GlobalStyles';

function LeadMapBubble({ price, isHovered, isClicked, isNew, verdict }) {
    // Verdict mode (Co-Living Deals feed): when a parking verdict is supplied,
    // the pin communicates eligibility at a glance — green = PASS, red = FAIL,
    // grey "?" = not yet determined — instead of the price chip. The clicked pin
    // keeps a pink ring so the active selection is still distinct from the
    // verdict colour. Callers that omit `verdict` (Leads, Spencer's Deals) fall
    // through to the price styling below unchanged.
    const hasVerdict = verdict === 'PASS' || verdict === 'FAIL' || verdict === 'UNKNOWN';
    const verdictStyles = useMemo(() => {
        if (!hasVerdict) return null;
        if (verdict === 'UNKNOWN') {
            return {
                backgroundColor: colour.gray01,
                borderColor: isClicked ? colour.pink03 : colour.gray03,
                borderWidth: isClicked ? '2px' : '1px',
                textColorClass: 'colour-secondary',
            };
        }
        const pass = verdict === 'PASS';
        return {
            backgroundColor: pass ? colour.green01 : colour.red01,
            borderColor: isClicked ? colour.pink03 : (pass ? colour.green02 : colour.red02),
            borderWidth: isClicked ? '2px' : (isHovered ? '1.5px' : '1px'),
            textColorClass: pass ? 'colour-green' : 'colour-error',
        };
    }, [hasVerdict, verdict, isClicked, isHovered]);

    // Three escalating tiers: neutral default, a subtle grey "lift" on hover,
    // and the brand pink reserved for the clicked/selected pin so the active
    // lead is the only saturated thing on the map (pink = lead, jade = comp).
    const bubbleStyles = useMemo(() => {
        if (isClicked) {
            // Selected - full brand pink, loudest state
            return {
                backgroundColor: colour.pink02,
                borderColor: colour.pink03,
                borderWidth: "2px",
                textColorClass: "colour-primary"
            };
        } else if (isHovered) {
            // Hover - subtle neutral lift (transient "you could click this")
            return {
                backgroundColor: colour.gray01,
                borderColor: colour.gray05,
                borderWidth: "1px",
                textColorClass: "colour-primary"
            };
        } else {
            // Default state - white with grey border
            return {
                backgroundColor: colour.gray00,
                borderColor: colour.gray03,
                borderWidth: "0.5px",
                textColorClass: "colour-primary"
            };
        }
    }, [isClicked, isHovered]);

    const formattedPrice = useMemo(() => {
        if (typeof price === 'number') {
            return formatterCompact.format(price);
        }
        return price || '-';
    }, [price]);

    const styles = verdictStyles || bubbleStyles;
    const label = hasVerdict
        ? (verdict === 'PASS' ? 'Pass' : verdict === 'FAIL' ? 'Fail' : '?')
        : formattedPrice;

    return (
        <div
            className={`lead-map-bubble-container${isNew ? ' lead-map-bubble-new' : ''}`}
            style={{
                backgroundColor: styles.backgroundColor,
                border: `${styles.borderWidth} solid ${styles.borderColor}`
            }}
        >
            {/* One-shot ripple ring for a just-arrived live deal. Sits behind
                the price chip and emanates from its centre. */}
            {isNew && <span className="lead-map-bubble-ripple" aria-hidden="true" />}
            <span className={`label-bold lead-map-bubble-price ${styles.textColorClass}`}>
                {label}
            </span>
        </div>
    );
}

export default React.memo(LeadMapBubble);
